/**
 * Green Flag — Chakra v3 system entry
 *
 * This is the single export that apps wire into `<ChakraProvider value={system}>`.
 * It merges Chakra's `defaultConfig` (so stock tokens stay available as a
 * safety net) with the Green Flag theme extension:
 *
 *   - brand + semantic color tokens
 *   - brand typography (Poppins + League Gothic), font sizes, line heights
 *   - radii, shadows, spacing scale
 *   - text styles (textStyle="hero" etc)
 *   - layer styles (layerStyle="card" etc)
 *   - component recipes (Button, Badge, Input)
 *   - slot recipes (PillTabs, EventCard, MatchCard)
 *
 * ### Usage
 *
 * ```tsx
 * import { ChakraProvider } from "@chakra-ui/react";
 * import { system } from "@green-flag/ui";
 *
 * <ChakraProvider value={system}>{...}</ChakraProvider>
 * ```
 */

import { createSystem, defaultConfig, defineConfig } from "@chakra-ui/react";

import { tokens } from "./Tokens";
import { semanticTokens } from "./SemanticTokens";
import { textStyles } from "./TextStyles";
import { layerStyles } from "./LayerStyles";

import { buttonRecipe } from "./recipes/ButtonRecipe";
import { badgeRecipe } from "./recipes/BadgeRecipe";
import { inputRecipe } from "./recipes/InputRecipe";

import { pillTabsRecipe } from "./slotRecipes/PillTabsRecipe";
import { eventCardRecipe } from "./slotRecipes/EventCardRecipe";
import { matchFillWidgetRecipe } from "./slotRecipes/MatchFillWidgetRecipe";
import { upcomingEligibleWidgetRecipe } from "./slotRecipes/UpcomingEligibleWidgetRecipe";
import { formFieldRecipe } from "./slotRecipes/FormFieldRecipe";

const config = defineConfig({
  globalCss: {
    "html, body": {
      fontFamily: "body",
      color: "fg.default",
      bg: "bg.canvas",
      margin: 0,
      minHeight: "100dvh",
    },
    "#root": {
      minHeight: "100dvh",
    },
    "*, *::before, *::after": { boxSizing: "border-box" },
    "h1 u, h2 u, h3 u": {
      textDecoration: "none",
      display: "inline-block",
    },
    "h1 u::after, h2 u::after, h3 u::after": {
      display: "block",
      width: "100%",
      height: "0.2em",
      position: "relative",
      background: "brand.green.light",
      content: '" "',
      zIndex: -1,
      top: "-0.25em",
    },
    "h1 u::after": {
      top: "-8px",
    },
    h1: {
      fontVariationSettings: '"wdth" 90',
    },
    li: {
      color: "brand.green.dark",
    },
  },
  theme: {
    tokens,
    semanticTokens,
    textStyles,
    layerStyles,
    recipes: {
      button: buttonRecipe,
      badge: badgeRecipe,
      input: inputRecipe,
    },
    slotRecipes: {
      pillTabs: pillTabsRecipe,
      eventCard: eventCardRecipe,
      matchFillWidget: matchFillWidgetRecipe,
      upcomingEligibleWidget: upcomingEligibleWidgetRecipe,
      formField: formFieldRecipe,
    },
  },
});

export const system = createSystem(defaultConfig, config);
